import { ChangeDetectionStrategy, Component, computed, OnInit, signal } from '@angular/core';
import { AbstractBasePanelComponent } from '../utils/abstract-base-panel.component';
import { CustomPackageDto, OrderDiscType, PackageOptionDto } from '../dto';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SkeletonScreenComponent } from '../skeleton-screen/skeleton-screen.component';
import { InputPanelComponent } from '../components/input-panel/input-panel.component';
import { PanelComponent } from '../panel/panel.component';
import { CommonModule } from '@angular/common';
import { camelCase } from 'lodash';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { NumberPorting } from '@webmodule-order/api';

const PHONE_NUMBER_PATTERN = /^(\d{10})+$/;

@Component({
  selector: 'webmodule-order-panel-phone',
  templateUrl: './panel-phone.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, PanelComponent, InputPanelComponent, ReactiveFormsModule, SkeletonScreenComponent, TranslatePipe],
  standalone: true,
})
export class PanelPhoneComponent extends AbstractBasePanelComponent<CustomPackageDto> implements OnInit {
  private readonly phoneNumbers = signal<NumberPorting[]>([]);
  protected readonly string = String;
  protected readonly camelCase = camelCase;
  readonly form = new FormGroup({
    phoneNumber: new FormControl(null, {
      nonNullable: true,
      validators: [Validators.pattern(PHONE_NUMBER_PATTERN)],
    }),
    secondPhoneNumber: new FormControl(null, {
      nonNullable: true,
      validators: [Validators.pattern(PHONE_NUMBER_PATTERN)],
    }),
  });
  readonly phoneLandline: CustomPackageDto[] = [
    {
      id: 'withoutLandline',
      name: 'Geen vaste telefonie',
      label: 'Nee, ik wil geen vaste telefonie',
      price: 0,
      disc: 'withoutLandline',
    },
    {
      id: 'withLandline',
      name: 'Ja, ik wil vaste telefonie',
      label: 'Ja, ik wil vaste telefonie',
      price: 0,
      disc: 'withLandline',
    },
  ];
  phoneOptions = computed(() => this.packageService.phoneOptions());
  selectedPhoneOptions = new Map<OrderDiscType, PackageOptionDto | null>();

  constructor() {
    super();

    this.setSelected(this.orderService.order.noPhone ? this.phoneLandline[0] : this.phoneLandline[1], 'landline');
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(distinctUntilChanged(), debounceTime(300)).subscribe(() => this.addPhoneNumbers());
  }

  addPhoneNumbers() {
    const phoneNumber = this.form.controls.phoneNumber;
    const secondPhoneNumber = this.form.controls.secondPhoneNumber;
    const phoneNumbersToPort: NumberPorting[] = [];

    if (phoneNumber.valid && phoneNumber.value) {
      phoneNumbersToPort.push({ phoneNumber: String(phoneNumber.value) });
    }

    if (secondPhoneNumber.valid && secondPhoneNumber.value) {
      phoneNumbersToPort.push({ phoneNumber: String(secondPhoneNumber.value) });
    }

    this.phoneNumbers.set(phoneNumbersToPort);

    this.updatePhoneOrderState();
  }

  isPhoneOptionsSelected(id: number | string, type: OrderDiscType) {
    const option = this.selectedPhoneOptions.get(type);

    if (!option) {
      return false;
    }

    return option.id === id;
  }

  override setSelected(value: CustomPackageDto, type: OrderDiscType) {
    super.setSelected(value, type);

    if (value.id === 'withoutLandline') {
      this.showSelected.set(false);
      for (const key of this.selectedPhoneOptions.keys()) {
        this.selectedPhoneOptions.set(key, null);
      }

      this.updatePhoneOrderState();
    }
  }

  getOptionByTag(tag: string) {
    return this.phoneOptions().find((option) => option.tag.includes(tag));
  }

  setExtraPhoneLineState(item: PackageOptionDto, type: OrderDiscType, required = true) {
    this.setPackageState(item, type, required);

    const value = this.selectedPhoneOptions.get(type);

    // Reset extraNumberPorting if extraPhoneLine is unset
    if (!value) {
      this.selectedPhoneOptions.set('extraNumberPorting', null);
    }

    this.updatePhoneOrderState();
  }

  setPackageState(item: PackageOptionDto, type: OrderDiscType, required = true) {
    const exist = this.selectedPhoneOptions.get(type);

    if (!exist) {
      this.selectedPhoneOptions.set(type, item);
    } else {
      if (required && exist.id === item.id) {
        return;
      }

      const value = exist.id === item.id ? null : item;

      this.selectedPhoneOptions.set(type, value);
    }

    this.addPhoneNumbers();
  }

  selectOptionAndClearPhoneNumber(control: string, item: PackageOptionDto, type: OrderDiscType): void {
    this.form.get(control)?.reset();
    this.setPackageState(item, type);
  }

  /**
   * iterate through options and update the orderState product can be null to remove an option from orderState
   */
  private updatePhoneOrderState() {
    for (const [key, product] of this.selectedPhoneOptions.entries()) {
      product && (product.value = 'true');
      this.updateOrder(product, key);
    }

    this.orderService.phoneNumbersToPort(this.phoneNumbers());
  }
}
